import { useCallback, useContext, useEffect, useRef } from 'react';

import type { Module } from 'simple-xdm/dist/host';

import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import type {
	AbortOptions,
	ExperienceTrackerAPI,
	FailOptions,
	StartOptions,
	SucceedOptions,
} from '@atlassian/experience-tracker/ExperienceTracker';

import {
	CHANNEL_NAME,
	EXPERIENCE_FORWARDING_MODULE_NAME,
	type ExperienceForwardingModule,
	PASSTHROUGH_PROPS_MODULE_NAME,
	type PassThroughPropsModule,
	PROPS_UPDATED,
	createUniqueKey,
} from './channel';
import type { IframePassThroughProps } from './IframeElementType';
import { fg } from '@atlaskit/platform-feature-flags';

let host: any = null;
// simple-xdm relies on window object. It is not available in SSR.
if (typeof window !== 'undefined') {
	host = require('simple-xdm/dist/host');
}

function defineModule<T extends Module>(name: string, module: T) {
	host.defineModule(name, module);
}

const useExperienceForwardingModule = () => {
	const experienceTrackerRef = useRef<ExperienceTrackerAPI>();
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (experienceTrackerRef.current !== experienceTracker) {
		defineModule<ExperienceForwardingModule>(EXPERIENCE_FORWARDING_MODULE_NAME, {
			start(options: StartOptions) {
				experienceTracker.start(options);
			},
			abort(options: AbortOptions) {
				experienceTracker.abort(options);
			},
			succeed(options: SucceedOptions) {
				experienceTracker.succeed(options);
			},
			fail(options: FailOptions) {
				experienceTracker.fail(options);
			},
		});

		experienceTrackerRef.current = experienceTracker;
	}
};

const usePassThroughPropsModule = (
	props: IframePassThroughProps & {
		onContentLoadStarted?: () => void;
	},
	uniqueKey: string,
) => {
	const { onContentLoadStarted, ...passThroughProps } = props;
	const dispatchPropsUpdated = useCallback(() => {
		host.dispatch(
			PROPS_UPDATED,
			{
				addon_key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME,
				...(fg('platform_deprecate_lp_cc_embed') && { key: uniqueKey }),
			},
			{
				message: JSON.stringify(passThroughProps),
			},
		);

		/**
		 * Disable react-hooks/exhaustive-deps linting, the dependency has passThroughProps spreading on its keys and values
		 * This should be able to handle any changes to the `passThroughProps` would recall the effect function.
		 **/
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...Object.keys(passThroughProps),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...Object.values(passThroughProps),
		uniqueKey,
	]);

	const dispatchPropsUpdatedRef = useRef<typeof dispatchPropsUpdated>();

	if (dispatchPropsUpdatedRef.current !== dispatchPropsUpdated) {
		defineModule<PassThroughPropsModule>(PASSTHROUGH_PROPS_MODULE_NAME, {
			getProps: () => {
				onContentLoadStarted?.();
				dispatchPropsUpdated();
			},
			navigationPolicyNavigate: passThroughProps.navigationPolicy?.navigate,
			onPublish: passThroughProps?.onPublish,
		});

		dispatchPropsUpdatedRef.current = dispatchPropsUpdated;
	}

	useEffect(dispatchPropsUpdated, [dispatchPropsUpdated]);
};

export function useIframeCommunicationChannel(
	iframeSrc: string,
	passThroughProps: Parameters<typeof usePassThroughPropsModule>[0],
) {
	const channelRef = useRef<ReturnType<typeof host.create>>();

	const uniqueKey = useRef(createUniqueKey()).current;

	useExperienceForwardingModule();
	usePassThroughPropsModule(passThroughProps, uniqueKey);

	if (channelRef.current?.src !== iframeSrc) {
		channelRef.current = host.create({
			addon_key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME,
			key: fg('platform_deprecate_lp_cc_embed') ? uniqueKey : CHANNEL_NAME,
			url: iframeSrc,
			options: {
				globalOptions: {},
				hostFrameOffset: 1,
				hostOrigin: window.location.origin,
			},
		});
	}
	return channelRef.current;
}
